'use client';

import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';
import { Eye, EyeOff } from 'lucide-react';

interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const [revealed, setRevealed] = useState(false);
    const inputId =
      id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-ui font-semibold uppercase tracking-wider text-ink-label"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={revealed ? 'text' : 'password'}
            className={clsx(
              'w-full rounded-[12px] bg-surface border border-outline-variant px-4 py-3 pr-11 text-sm font-ui text-ink outline-none transition-all',
              'placeholder:text-ink-muted',
              'focus:border-primary focus:ring-1 focus:ring-primary/20',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface-container',
              error && 'ring-1 ring-error/40',
              className,
            )}
            aria-invalid={error ? true : undefined}
            aria-describedby={error && inputId ? `${inputId}-error` : undefined}
            {...props}
          />
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            disabled={props.disabled}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            /*
             * UI-04: NO `tabIndex={-1}` here. Removing the toggle from the tab
             * order made it pointer-only, so keyboard and switch users could
             * never reveal a password they had typed — a WCAG 2.1.1 failure on
             * every auth form — while the `aria-label`, `aria-pressed` and
             * focus-visible ring below could never apply to anything.
             */
            className="absolute inset-y-0 right-0 flex items-center justify-center px-3 text-ink-muted hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
        {error && (
          <p
            id={inputId ? `${inputId}-error` : undefined}
            className="text-xs font-ui text-error"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
export type { PasswordInputProps };
